/**
 * 공통 API 에러 핸들러
 * 일관된 에러 처리 제공
 */

/**
 * @typedef {Object} ErrorContext
 * @property {string} service - 서비스 이름
 * @property {string} method - 메서드 이름
 * @property {Object} [params] - 요청 파라미터
 */

class ApiErrorHandler {
  /**
   * API 에러 처리
   * @param {Error} error - 에러 객체
   * @param {ErrorContext} context - 에러 컨텍스트
   * @returns {Error} 처리된 에러 객체
   */
  static handleError(error, context) {
    const { service, method, params } = context;
    
    // 로깅
    console.error(`[${service}.${method}] API 요청 실패:`, {
      error: error.message,
      params,
      response: error.response?.data
    });
    
    // 네트워크 에러인 경우
    if (!error.response) {
      error.message = '네트워크 연결을 확인해주세요';
      error.code = 'NETWORK_ERROR';
      return error;
    }
    
    // HTTP 상태 코드별 처리
    const status = error.response.status;
    const responseData = error.response.data;
    
    switch (status) {
      case 400:
        error.message = responseData?.message || '잘못된 요청입니다';
        error.code = 'BAD_REQUEST';
        break;
        
      case 401:
        error.message = responseData?.message || '인증이 필요합니다';
        error.code = 'UNAUTHORIZED';
        // 인증 에러의 경우 추가 처리 필요할 수 있음
        break;
        
      case 403:
        error.message = responseData?.message || '접근 권한이 없습니다';
        error.code = 'FORBIDDEN';
        break;
        
      case 404:
        error.message = responseData?.message || '요청한 데이터를 찾을 수 없습니다';
        error.code = 'NOT_FOUND';
        break;
        
      case 409:
        error.message = responseData?.message || '데이터 충돌이 발생했습니다';
        error.code = 'CONFLICT';
        break;
        
      case 422:
        error.message = responseData?.message || '입력 데이터가 유효하지 않습니다';
        error.code = 'VALIDATION_ERROR';
        // 필드별 검증 에러가 있다면 추가
        if (responseData?.errors) {
          error.validationErrors = responseData.errors;
        }
        break;
        
      case 429:
        error.message = '요청이 너무 많습니다. 잠시 후 다시 시도해주세요';
        error.code = 'RATE_LIMIT';
        break;
        
      case 500:
        error.message = responseData?.message || '서버 오류가 발생했습니다';
        error.code = 'INTERNAL_SERVER_ERROR';
        break;
        
      case 502:
      case 503:
      case 504:
        error.message = '서버가 일시적으로 사용할 수 없습니다';
        error.code = 'SERVICE_UNAVAILABLE';
        break;
        
      default:
        error.message = responseData?.message || '알 수 없는 오류가 발생했습니다';
        error.code = 'UNKNOWN_ERROR';
    }
    
    // 원본 상태 코드 보존
    error.statusCode = status;
    error.originalResponse = responseData;
    
    return error;
  }

  /**
   * 재시도 가능한 에러인지 확인
   * @param {Error} error 
   * @returns {boolean}
   */
  static isRetryableError(error) {
    const retryableCodes = [
      'NETWORK_ERROR',
      'SERVICE_UNAVAILABLE',
      'RATE_LIMIT'
    ];
    
    const retryableStatus = [408, 429, 500, 502, 503, 504];
    
    return retryableCodes.includes(error.code) || 
           retryableStatus.includes(error.statusCode);
  }

  /**
   * HTTP 상태 코드를 직접 처리하여 사용자 메시지 생성
   * @param {Error} error - 원본 에러 객체 (axios 에러)
   * @returns {string} 사용자 친화적 메시지
   */
  static handleHttpError(error) {
    // 먼저 응답 데이터에서 메시지 확인
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    
    // HTTP 상태 코드별 표준화된 메시지
    const status = error.response?.status;
    switch (status) {
      case 401:
        return '로그인이 필요합니다.';
      case 403:
        return '접근 권한이 없습니다.';
      case 404:
        return '요청한 정보를 찾을 수 없습니다.';
      case 422:
        return '입력하신 정보를 다시 확인해주세요.';
      case 429:
        return '잠시 후 다시 시도해주세요.';
      case 500:
      case 502:
      case 503:
      case 504:
        return '서버에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
      default:
        if (status >= 500) {
          return '서버에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
        }
        return '네트워크 오류가 발생했습니다. 연결을 확인해주세요.';
    }
  }

  /**
   * 사용자에게 표시할 메시지 생성 (표준화된 버전)
   * @param {Error} error 
   * @returns {string}
   */
  static getUserMessage(error) {
    // 사용자 친화적 메시지로 변환 (표준화된 메시지)
    switch (error.code) {
      case 'NETWORK_ERROR':
        return '네트워크 오류가 발생했습니다. 연결을 확인해주세요.';
        
      case 'VALIDATION_ERROR':
        return '입력하신 정보를 다시 확인해주세요.';
        
      case 'NOT_FOUND':
        return '요청한 정보를 찾을 수 없습니다.';
        
      case 'UNAUTHORIZED':
        return '로그인이 필요합니다.';
        
      case 'FORBIDDEN':
        return '접근 권한이 없습니다.';
        
      case 'RATE_LIMIT':
        return '잠시 후 다시 시도해주세요.';
        
      case 'SERVICE_UNAVAILABLE':
      case 'INTERNAL_SERVER_ERROR':
        return '서버에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
        
      default:
        return error.message || '문제가 발생했습니다.';
    }
  }
}

export default ApiErrorHandler;